import { Directive, inject } from '@angular/core';
import { AbstractCheckoutCreditCardForm } from '@df/checkout/abstract-checkout-credit-card-form.component';
import { Checkout } from '@df/checkout/checkout.interfaces';
import { NSagepay } from '../sagepay.namespace';
import { SagepayPiService } from './sagepay-pi.service';

@Directive()
export abstract class SagepayPiComponentDef<
    I extends NSagepay.ISubmissionPaymentData<NSagepay.ISubmissionAdditionalPaymentData>,
    D
> extends AbstractCheckoutCreditCardForm<NSagepay.IPiFormPaymentMethod, D> {
    protected methodCode = NSagepay.Methods.PiForm;

    protected sagepayPiService = inject(SagepayPiService);

    /**
     * Returns sagepay api script url
     * @returns {string}
     */
    protected getJsAssets(): string[] {
        return [this.method.isTest ? NSagepay.Scripts.PI_FORM_TEST : NSagepay.Scripts.PI_FORM];
    }

    /**
     * Returns allowed card types
     * @returns {string[]}
     */
    getAllowedCcTypes(): string[] {
        return this.method.ccTypes || [];
    }

    /**
     * If payment method should be initiated before selecting
     * @returns {boolean}
     */
    protected preload(): boolean {
        return true;
    }

    /**
     * Returns checkout submission payment data
     * @param {NSagepay.IOwnFormTokeniseResult} tokeniseResult
     * @param {string} merchantKey
     * @returns {NSagepay.ISubmissionPaymentData<NSagepay.ISubmissionAdditionalPaymentData>}
     */
    protected abstract getSubmissionPaymentData(tokeniseResult: NSagepay.ITokeniseResult, merchantKey: string): I;

    /**
     * On checkout submission hook
     * @param {Checkout.ISubmitBeforeEventData} checkoutSubmitBeforeEventData
     * @returns {false | void}
     */
    protected onCheckoutSubmit(checkoutSubmitBeforeEventData: Checkout.ISubmitBeforeEventData): false | void {
        if (super.onCheckoutSubmit(checkoutSubmitBeforeEventData) === false) {
            return false;
        }

        checkoutSubmitBeforeEventData.continueSubmission = false;
        const status = this.checkoutFormComponent.status;
        status.busy = true;

        const data = { ...this.data };

        this.sagepayPiService
            .generateMerchantKey()
            .then(response => {
                if (response?.spMerchantKey) {
                    this.getTokeniseResult(response, data)
                        .then(tokeniseResult => {
                            checkoutSubmitBeforeEventData.data.paymentMethod = this.getSubmissionPaymentData(
                                tokeniseResult,
                                response.spMerchantKey
                            );
                            status.busy = false;
                            checkoutSubmitBeforeEventData.next();
                        })
                        .catch(e => {
                            status.error = this.sagepayPiService.getError(e);
                        });
                } else {
                    status.error = this.sagepayPiService.getError();
                }
            })
            .catch(() => {
                status.error = this.sagepayPiService.getError();
            });
    }

    protected abstract getTokeniseResult(merchantKeyResponse: NSagepay.IMerchantKeyResponse, data: D): Promise<NSagepay.ITokeniseResult>;
}
